/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import { hideSpinner } from '../spinner/spinner.reducers';
import { EXIT_PLAYER_THRESHOLD_TIME, STILL_THERE_THRESHOLD_TIME } from '../../config';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { PlaybackState, TrickPlayType } from '../videoplayerui/infobanner/PlaybackTypes';
import {
    setScreenTaskController,
    showGuideScreen,
    showLiveTvScreen,
} from '../screens/screens.reducers';
import { Log } from '../../core/logging/Log';
import {
    videoPlayerStartPlaybackAction,
    videoPlayerRestartPlaybackAction,
    videoPlayerResetRetryAction,
    videoPlayerToggleIsPlayingAction,
    videoPlayerSetPlaybackStateAction,
    setExitPlayerTimeoutAction,
    resetStillThereTimeoutAction,
    showStillThereOsdAction,
    hideStillThereOsdAction,
    videoPlayerIsMediaButtonPressedAction,
    isStillThereAction,
} from './videoplayer.actions';
import {
    VIDEO_PLAYER_START_PLAYBACK,
    VIDEO_PLAYER_RESTART_PLAYBACK,
    VIDEO_PLAYER_RESET_RETRY,
    VIDEO_PLAYER_TOGGLE_IS_PLAYING,
    VIDEO_PLAYER_SET_PLAYBACK_STATE,
    UPDATE_STILL_THERE_TIMER,
    SET_EXIT_PLAYER_TIMER,
    SHOW_STILL_THERE_OSD,
    HIDE_STILL_THERE_OSD,
    VIDEO_PLAYER_IS_PAUSE_BUTTON_PRESSED,
    IS_STILL_THERE,
} from './videoplayer.types';
import { Channel } from '../../core/entities/Channel';
import { VuLogger } from '../../core/logging/VULogger';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { PlaybackContentType } from '../../core/socu/enums/PlaybackContentType';
import { SocuDataModelImpl } from '../../core/socu/impls';
import { VideoManager } from '../../core/videoplayer';
import { TrickplayImpl } from '../../core/videoplayer/impls';
import { resetAntiburnTimeout } from '../antiburnosd/antiburnosd.reducers';

const initialState: {
    channel: Channel;
    retry: number;
    isPlaying: boolean;
    exitPlayerTimer: any;
    stillThereTimer: any;
    isStillThere: boolean;
    playbackState: PlaybackState;
    isStillThereOsdActive: boolean;
    mediaButtonPressed: {
        type: TrickPlayType;
        isMediaButtonPressed: boolean;
    };
} = {
    channel: {} as Channel,
    retry: 0,
    isPlaying: true,
    playbackState: PlaybackState.PLAY,
    exitPlayerTimer: null,
    stillThereTimer: null,
    isStillThereOsdActive: false,
    isStillThere: true,
    mediaButtonPressed: {
        type: TrickPlayType.NONE,
        isMediaButtonPressed: false,
    },
};

const TAG: string = 'VIDEOPLAYER';

const videoPlayerReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case VIDEO_PLAYER_START_PLAYBACK:
            return {
                ...state,
                channel: payload.channel,
                retry: 0,
            };

        case VIDEO_PLAYER_RESTART_PLAYBACK:
            return {
                ...state,
                channel: payload.channel,
                retry: payload.retry,
            };

        case VIDEO_PLAYER_RESET_RETRY:
            return {
                ...state,
                retry: 0,
            };

        case VIDEO_PLAYER_TOGGLE_IS_PLAYING:
            return {
                ...state,
                isPlaying: payload.isPlaying,
            };
        case VIDEO_PLAYER_SET_PLAYBACK_STATE:
            return {
                ...state,
                playbackState: payload.playbackState,
            };
        case SHOW_STILL_THERE_OSD:
        case HIDE_STILL_THERE_OSD:
            return {
                ...state,
                isStillThereOsdActive: payload.isStillThereOsdActive,
            };
        case UPDATE_STILL_THERE_TIMER:
            return {
                ...state,
                stillThereTimer: payload.stillThereTimer,
            };
        case IS_STILL_THERE:
            return {
                ...state,
                isStillThere: payload.isStillThere,
            };

        case SET_EXIT_PLAYER_TIMER:
            return {
                ...state,
                exitPlayerTimer: payload.exitPlayerTimer,
            };
        case VIDEO_PLAYER_IS_PAUSE_BUTTON_PRESSED:
            return {
                ...state,
                mediaButtonPressed: payload.mediaButtonPressed,
            };

        default:
            return state;
    }
};

export const hideNotSupportedText = () => (dispatch: Function, getState: Function) => {
    const { mediaButtonPressed } = getState().videoPlayer;
    if (mediaButtonPressed.isMediaButtonPressed) {
        dispatch(
            videoPlayerIsMediaButtonPressedAction({
                type: TrickPlayType.NONE,
                isMediaButtonPressed: false,
            }),
        );
    }
};

export const switchToLiveTv = () => (dispatch: Function) => {
    VideoManager.getInstance().updateLastViewedPosition();
    SocuDataModelImpl.getInstance().resetSocuData();
    VideoManager.getInstance().startPlayback({
        channel: VideoManager.getInstance().getPlayable()?.channel!,
        playbackContentType: PlaybackContentType.LIVE,
    });
};

export const startVideoPlayback = (channel: Channel) => (dispatch: Function) => {
    if (channel && channel.channelNumber) {
        dispatch(videoPlayerStartPlaybackAction(channel));
        dispatch(showLiveTvScreen());
        dispatch(resetAntiburnTimeout());
    }
};

export const retryVideoPlayback = () => (dispatch: Function, getState: Function) => {
    const { channel, retry } = getState().videoPlayer;
    dispatch(videoPlayerRestartPlaybackAction(channel, retry + 1));
};

export const toggleClosedCaption = () => () => {
    Diagnostics.info(TAG, 'toggleClosedCaption');
    Log.i(TAG, 'toggleClosedCaption');
    VideoManager.getInstance().getVideoPlayerModel().toggleClosedCaption();
};

export const changeClosedCaptionLanguage = (selectedIndex: number) => () => {
    VideoManager.getInstance().getVideoPlayerModel().setCurrentTextTrack(selectedIndex);
};

export const changeAudioTrack = (selectedIndex: number) => () => {
    Diagnostics.info(TAG, 'changeAudioTrack at index::', selectedIndex);
    Log.i(TAG, 'changeAudioTrack at index::', selectedIndex);
    VideoManager.getInstance().getVideoPlayerModel().setCurrentAudioTrack(selectedIndex);
};

export const resetPlaybackRetry = () => (dispatch: Function) => {
    dispatch(videoPlayerResetRetryAction());
};

export const toggleIsPlaying = () => (dispatch: Function, getState: Function) => {
    const { isPlaying, playbackState } = getState().videoPlayer;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    if (isPlaying) {
        VideoManager.getInstance().pause(true);
        dispatch(disableStillThereTimer());
        if (SocuDataModelImpl.getInstance().getPlaybackContentType() !== PlaybackContentType.LIVE) {
            VideoManager.getInstance().updateLastViewedPosition();
        }
    } else {
        VideoManager.getInstance().unPause();
        dispatch(resetStillThereTimeout(true));
        if (videoPlayerIns.isPlaybackStarted()) {
            videoPlayerIns.resetVolumeSpeed(playbackState);
        }
    }
    Diagnostics.info(TAG, 'toggleIsPlaying:', !isPlaying);
    Log.i(TAG, 'toggleIsPlaying:', !isPlaying);
    dispatch(videoPlayerToggleIsPlayingAction(!isPlaying));
    dispatch(
        videoPlayerSetPlaybackStateAction(isPlaying ? PlaybackState.PAUSE : PlaybackState.PLAY),
    );
    VuLogger.logTrickPlayEvent(
        isPlaying ? VuLogger.TrickplayMode.PAUSE : VuLogger.TrickplayMode.PLAY,
        videoPlayerIns.getCurrentTimeInMs(),
    );
};

export const resetPlaybackParamsVideoplayer = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    videoPlayerIns.resetVolumeSpeed(playbackState);
    dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.PLAY));
    videoPlayerIns.unPause();
    dispatch(videoPlayerToggleIsPlayingAction(true));
    dispatch(resetStillThereTimeout(true));
};

export const handleFastForward = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const { isSpinnerActive } = getState().spinner;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    dispatch(hideNotSupportedText());
    if (isSpinnerActive) dispatch(hideSpinner());

    Log.i(TAG, `Playing with ${playbackState}`);
    Diagnostics.info(TAG, `Playing with ${playbackState}`);
    if (playbackState === PlaybackState.FF1) {
        videoPlayerIns.selectSpeed(PlaybackState.FF2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_2,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.FF2) {
        videoPlayerIns.selectSpeed(PlaybackState.FF3);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF3));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_3,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.FF3) {
        Diagnostics.info(TAG, `Already playing with maximum fast forward speed 3.`);
        Log.i(TAG, `Already playing with maximum fast forward speed 3.`);
    } else if (playbackState === PlaybackState.RW3) {
        videoPlayerIns.selectSpeed(PlaybackState.RW2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_2,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.RW2) {
        videoPlayerIns.selectSpeed(PlaybackState.RW1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_1,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.RW1) {
        dispatch(toggleIsPlaying());
    } else {
        videoPlayerIns.mute();
        dispatch(videoPlayerToggleIsPlayingAction(false));
        videoPlayerIns.selectSpeed(PlaybackState.FF1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_1,
            videoPlayerIns.getCurrentTimeInMs(),
        );
        QoEAppLogger.updateHealthStatsMetricsForTrickPlay(Date.now(), 0);
    }
};

export const handleRewind = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const { isSpinnerActive } = getState().spinner;
    const videoPlayerIns = VideoManager.getInstance().getVideoPlayerModel();
    dispatch(hideNotSupportedText());
    if (isSpinnerActive) dispatch(hideSpinner());

    Log.i(TAG, `Playing with ${playbackState}`);
    Diagnostics.info(TAG, `Playing with ${playbackState}`);
    if (playbackState === PlaybackState.RW1) {
        videoPlayerIns.selectSpeed(PlaybackState.RW2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_2,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.RW2) {
        videoPlayerIns.selectSpeed(PlaybackState.RW3);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW3));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_3,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.RW3) {
        Diagnostics.info(TAG, `Already playing with maximum rewind speed 3.`);
        Log.i(TAG, `Already playing with maximum rewind speed 3.`);
    } else if (playbackState === PlaybackState.FF3) {
        videoPlayerIns.selectSpeed(PlaybackState.FF2);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF2));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_2,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.FF2) {
        videoPlayerIns.selectSpeed(PlaybackState.FF1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.FF1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.FAST_FORWARD_1,
            videoPlayerIns.getCurrentTimeInMs(),
        );
    } else if (playbackState === PlaybackState.FF1) {
        dispatch(toggleIsPlaying());
    } else {
        videoPlayerIns.mute();
        dispatch(videoPlayerToggleIsPlayingAction(false));
        videoPlayerIns.selectSpeed(PlaybackState.RW1);
        dispatch(videoPlayerSetPlaybackStateAction(PlaybackState.RW1));
        VuLogger.logTrickPlayEvent(
            VuLogger.TrickplayMode.REWIND_1,
            videoPlayerIns.getCurrentTimeInMs(),
        );
        QoEAppLogger.updateHealthStatsMetricsForTrickPlay(Date.now(), 0);
    }
};

export const handleReplay = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const videoManagerIns = VideoManager.getInstance();
    const videoPlayerIns = videoManagerIns.getVideoPlayerModel();
    dispatch(hideNotSupportedText());
    Diagnostics.info(TAG, 'handleReplay');
    Log.i(TAG, 'handleReplay');
    if (
        playbackState === PlaybackState.RW1 ||
        playbackState === PlaybackState.RW2 ||
        playbackState === PlaybackState.RW3
    ) {
        const trickplayTime = videoPlayerIns.getTrickPlayTimeInSeconds();
        const trickplayImplIns = new TrickplayImpl(
            videoManagerIns.getPlayable(),
            videoPlayerIns.getPlaybackIntervalInfo(),
        );
        const position = trickplayImplIns.getSkipPositionForRewind();
        videoPlayerIns.setTrickPlayTimeInSeconds(
            trickplayImplIns.calculateTrickplayTimeFromPositionInSeconds(position, trickplayTime),
        );
        videoPlayerIns.seekTo(position);
    } else {
        videoPlayerIns.seekBackward();
        if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
    }
};

export const handleAdvance = () => (dispatch: Function, getState: Function) => {
    const { playbackState } = getState().videoPlayer;
    const videoManagerIns = VideoManager.getInstance();
    const videoPlayerIns = videoManagerIns.getVideoPlayerModel();
    dispatch(hideNotSupportedText());
    if (
        playbackState === PlaybackState.FF1 ||
        playbackState === PlaybackState.FF2 ||
        playbackState === PlaybackState.FF3
    ) {
        const trickplayImplIns = new TrickplayImpl(
            videoManagerIns.getPlayable(),
            videoPlayerIns.getPlaybackIntervalInfo(),
        );
        const trickplayTime = videoPlayerIns.getTrickPlayTimeInSeconds();
        const position = trickplayImplIns.getSkipPositionForForward();
        videoPlayerIns.setTrickPlayTimeInSeconds(
            trickplayImplIns.calculateTrickplayTimeFromPositionInSeconds(position, trickplayTime),
        );
        videoPlayerIns.seekTo(position);
    } else if (
        playbackState === PlaybackState.RW1 ||
        playbackState === PlaybackState.RW2 ||
        playbackState === PlaybackState.RW3
    ) {
        const trickplayImplIns = new TrickplayImpl(
            videoManagerIns.getPlayable(),
            videoPlayerIns.getPlaybackIntervalInfo(),
        );
        const position = trickplayImplIns.getSkipPositionForRewind();
        const trickplayTime = videoPlayerIns.getTrickPlayTimeInSeconds();
        videoPlayerIns.setTrickPlayTimeInSeconds(
            trickplayImplIns.calculateTrickplayTimeFromPositionInSeconds(position, trickplayTime),
        );
        videoPlayerIns.seekTo(position);
    } else {
        videoPlayerIns.seekForward();
        if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
    }
};

export const showStillThereOsd = () => (dispatch: Function) => {
    Diagnostics.debug(
        TAG,
        'showStillThereOsd {screenTaskController: ',
        ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS,
        '}',
    );
    Log.d(
        TAG,
        'showStillThereOsd {screenTaskController: ',
        ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS,
        '}',
    );
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS));
    VuLogger.logNotifyEvent(
        VuLogger.NotifyEventSourceType.STILL_THERE_OSD,
        VuLogger.NotifyEventSourceName.STILL_THERE,
    );
    dispatch(showStillThereOsdAction());
};

export const hideStillThereOsd = () => (dispatch: Function, getState: Function) => {
    const { uiProperties: uiProps, isOverlayDialogActive } = getState().overlayDialog;

    const isAudioTrackOverlayActive =
        isOverlayDialogActive && uiProps.overlayType === OverlayTypes.AUDIO_TRACK;

    const isSubtitleAndCCLanguageOverlayActive =
        isOverlayDialogActive && uiProps.overlayType === OverlayTypes.SUBTITLE_AND_CC_LANGUAGE;

    Diagnostics.info(TAG, 'hideStillThereOsd ');
    Log.i(TAG, 'hideStillThereOsd ');
    if (isSubtitleAndCCLanguageOverlayActive) {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY,
            '}',
        );
        dispatch(
            setScreenTaskController(ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY),
        );
    } else if (isAudioTrackOverlayActive) {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY));
    } else {
        Diagnostics.debug(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        Log.d(
            TAG,
            'hideStillThereOsd {screenTaskController: ',
            ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS,
            '}',
        );
        dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS));
    }
    dispatch(hideStillThereOsdAction());
};

export const disableStillThereTimer = () => (dispatch: Function, getState: Function) => {
    const { videoPlayer } = getState();
    const { stillThereTimer, exitPlayerTimer, isStillThereOsdActive } = videoPlayer;

    dispatch(isStillThereAction(false));

    if (isStillThereOsdActive) dispatch(hideStillThereOsd());

    if (stillThereTimer) clearTimeout(stillThereTimer);
    dispatch(resetStillThereTimeoutAction(null));

    if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
    dispatch(setExitPlayerTimeoutAction(null));

    Diagnostics.info(TAG, 'disableStillThereTimer ');
    Log.i(TAG, 'disableStillThereTimer ');
};

export const resetStillThereTimeout =
    (isEnable?: boolean) => (dispatch: Function, getState: Function) => {
        if (isEnable) dispatch(isStillThereAction(isEnable));
        const { videoPlayer, screens } = getState();
        const { screenController } = screens;
        const { stillThereTimer, exitPlayerTimer, isStillThereOsdActive, isStillThere } =
            videoPlayer;
        const isStillThereEnable = isEnable || isStillThere;
        if (!isStillThereOsdActive) {
            if (stillThereTimer) clearTimeout(stillThereTimer);
            if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
            if (screenController === ScreenControllerTypes.VIDEO_PLAYER && isStillThereEnable) {
                const timer = setTimeout(() => {
                    dispatch(showStillThereOsd());
                    dispatch(setExitPlayerTimeout());
                }, STILL_THERE_THRESHOLD_TIME);
                dispatch(resetStillThereTimeoutAction(timer));
            } else {
                if (stillThereTimer) dispatch(resetStillThereTimeoutAction(null));
                if (exitPlayerTimer) dispatch(setExitPlayerTimeoutAction(null));
            }
        }
        Diagnostics.info(TAG, 'resetStillThereTimeout ');
        Log.i(TAG, 'resetStillThereTimeout ');
    };

export const setExitPlayerTimeout = () => (dispatch: Function, getState: Function) => {
    const { videoPlayer } = getState();
    const { exitPlayerTimer } = videoPlayer;
    if (exitPlayerTimer) clearTimeout(exitPlayerTimer);
    const timer = setTimeout(() => {
        dispatch(hideStillThereOsd());
        dispatch(showGuideScreen());
        dispatch(resetAntiburnTimeout());
    }, EXIT_PLAYER_THRESHOLD_TIME);
    dispatch(setExitPlayerTimeoutAction(timer));
    Diagnostics.info(TAG, 'setExitPlayerTimeout ');
    Log.i(TAG, 'setExitPlayerTimeout ');
};

export default videoPlayerReducer;
